import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
  Paper
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DeleteDepartmentById, GetDepartmentList } from '../network/Department';
import ConfirmAlertDialog from './ConfirmDialog';
import { HeaderHeight, PageHeaderHeight } from '../Constants';

import NoDataAvailable from './_dashboard/smartlock/NoDataAvailable';
import AddNewDepartment from './_dashboard/smartlock/AddNewDeparment';

import DepartmentMoreMenu from './_dashboard/smartlock/DepartmentMoreMenu';
import DeviceAssignModal from './_dashboard/smartlock/DeviceAssignModal';

const useStyles = makeStyles((theme) => ({
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  },
  tableCellStyle: {
    fontSize: {
      xs: '12px',
      md: '14px'
    },
    fontWeight: 700,
    padding: {
      xs: '8px',
      md: '16px'
    },
    textWrap: 'nowrap',
    backgroundColor: '#FFFFFF'
  },
  tablePaper: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF'
  },
  tableContainer: {
    flexGrow: 1,
    height: `calc(100vh - ${HeaderHeight + PageHeaderHeight + 56 + 40 + 50 + 18}px)`,
    overflow: 'auto'
  }
}));

const LoaderContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '50px'
});

const PageHeader = styled('Box')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 1,
  flexDirection: 'row !important',
  height: PageHeaderHeight
}));

export default function DepartmentMangement() {
  const classes = useStyles();
  const [departmentList, setDepartmentList] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortFilterValue, setSortFilterValue] = useState(['created', 'DESC']);
  const [isAssignAddModal, setIsAssignAddModal] = useState(false);
  const [isDeviceAssignModal, setIsDeviceAssignModal] = useState(false);
  const [openConfirmAlertDialog, setOpenConfirmAlertDialog] = useState(false);
  const [departmentDetail, setDepartmentDetail] = useState({});
  const [loader, setLoader] = useState(false);
  const [isStatusActive, setIsStatusActive] = useState(true);
  const [confirmAlertDialogMetas, setConfirmAlertDialogMetas] = useState({
    message: '',
    title: '',
    positiveBtnLable: '',
    negativeBtnLabel: ''
  });
  const [alertMetas, setAlertMetas] = useState({
    snackBarOpened: false,
    msg: '',
    variant: 'filled',
    severity: ''
  });

  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertMetas({ ...alertMetas, snackBarOpened: false });
  };

  const handleFilterChange = (e) => {
    const value = e.target.value.split(',');
    setSortFilterValue(value);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleUserMoreMenuClick = (action, data) => {
    switch (action) {
      case 'device_assign': {
        setDepartmentDetail(data);
        setIsDeviceAssignModal(true);
        break;
      }
      case 'edit_department': {
        setDepartmentDetail(data);
        setIsAssignAddModal(true);
        break;
      }
      case 'delete_department': {
        setDepartmentDetail(data);
        setConfirmAlertDialogMetas({
          ...confirmAlertDialogMetas,
          message: 'Are you sure want to delete this department?',
          title: 'Delete Department',
          positiveBtnLable: 'Confirm',
          negativeBtnLabel: 'Cancel'
        });
        setOpenConfirmAlertDialog(true);
        break;
      }
      default:
        return '';
    }
  };

  const deleteDepartment = async (id) => {
    try {
      const res = await DeleteDepartmentById(id);
      setAlertMetas({
        severity: 'success',
        snackBarOpened: true,
        msg: res?.data?.data || 'Department deleted successfully'
      });

      setTimeout(() => {
        setAlertMetas((prev) => ({ ...prev, snackBarOpened: false, msg: '' }));
      }, 4000);
      onAddNewDepartmentAction();
    } catch (error) {
      setAlertMetas({
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message || 'Something went wrong while deleting'
      });

      setTimeout(() => {
        setAlertMetas((prev) => ({ ...prev, snackBarOpened: false, msg: '' }));
      }, 4000);
    }
  };

  const getAllDepartment = async (page, sortFilterValue) => {
    try {
      setLoader(true);
      const response = await GetDepartmentList({ page, sortFilter: sortFilterValue });
      if (response?.data) {
        setDepartmentList(response.data);
      } else {
        console.warn('No department data received.');
        setDepartmentList([]);
      }
    } catch (error) {
      console.error('Failed to fetch department list:', error);
    } finally {
      setLoader(false);
    }
  };

  const onAddNewDepartmentAction = () => {
    getAllDepartment(page, sortFilterValue);
  };

  useEffect(() => {
    getAllDepartment(page, sortFilterValue);
  }, [page, sortFilterValue]);

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={alertMetas?.snackBarOpened}
        onClose={handleSnackBarClose}
        autoHideDuration={6000}
      >
        <Alert
          onClose={handleSnackBarClose}
          variant={alertMetas?.variant}
          severity={alertMetas?.severity}
        >
          {alertMetas?.msg}
        </Alert>
      </Snackbar>
      <Card sx={{ width: '100%', padding: '10px' }}>
        <Box>
          <PageHeader>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Button
                variant="outlined"
                size="medium"
                endIcon={<PersonAddAltOutlinedIcon />}
                onClick={() => setIsAssignAddModal(true)}
                sx={{ backgroundColor: '#FFFFFF' }}
              >
                Add
              </Button>
            </Stack>

            <FormControl
              data-testid="department-sort-formControl"
              size="small"
              sx={{ minWidth: { xs: 130, sm: 150 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 600 }}>
                  Sort By
                </Typography>
                <Select
                  sx={{
                    fontSize: { xs: '12px', sm: '14px' },
                    fontWeight: '500',
                    minWidth: '130px',
                    borderRadius: '12px'
                  }}
                  labelId="role-filter-select-label"
                  id="role-filter-select"
                  value={sortFilterValue.join(',')}
                  onChange={handleFilterChange}
                  IconComponent={ExpandMoreIcon}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 180,
                        overflowY: 'auto'
                      }
                    }
                  }}
                >
                  <MenuItem className={classes.menuItem} value="created,DESC">
                    Most Recent
                  </MenuItem>
                  <MenuItem className={classes.menuItem} value="created,ASC">
                    Oldest
                  </MenuItem>
                  <MenuItem className={classes.menuItem} value="name,ASC">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%'
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                        Organization Name
                      </Typography>{' '}
                      <ExpandMoreIcon />
                    </Box>
                  </MenuItem>
                  <MenuItem className={classes.menuItem} value="name,DESC">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%'
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                        Organization Name
                      </Typography>{' '}
                      <ExpandLessIcon />
                    </Box>
                  </MenuItem>
                </Select>
              </Box>
            </FormControl>
          </PageHeader>
          <Box sx={{ width: '100%', typography: 'body1', borderTop: '1px solid #EBEBEB' }}>
            <Paper className={classes.tablePaper}>
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCellStyle} width={100}>
                        Name
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} minWidth={500}>
                        Detail
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} width={100} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loader && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <LoaderContainer>
                            <CircularProgress size={30} />
                          </LoaderContainer>
                        </TableCell>
                      </TableRow>
                    )}
                    {departmentList?.data?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <NoDataAvailable />
                        </TableCell>
                      </TableRow>
                    ) : (
                      departmentList?.data?.map((item) => (
                        <TableRow key={item?.id}>
                          <TableCell
                            align="left"
                            sx={{
                              textTransform: 'capitalize',
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                          >
                            {item?.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: '5vw',
                              textWrap: 'wrap',
                              textTransform: 'capitalize',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                            align="left"
                          >
                            {item.description ? item.description : 'No description available'}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              textTransform: 'capitalize',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                            align="right"
                          >
                            <DepartmentMoreMenu
                              handleClicks={(e) => handleUserMoreMenuClick(e, item)}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {departmentList?.data?.length !== 0 && (
              <Stack
                sx={{
                  marginTop: '10px',
                  marginBottom: '10px'
                }}
              >
                <Pagination
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                  count={departmentList?.totalPages}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  page={page}
                  total={departmentList?.total}
                />
              </Stack>
            )}
          </Box>
        </Box>
      </Card>

      {isAssignAddModal && (
        <AddNewDepartment
          openAssignedModal={isAssignAddModal}
          handleClose={() => setIsAssignAddModal(false)}
          setAlertMetas={setAlertMetas}
          alertMetas={alertMetas}
          onAction={() => onAddNewDepartmentAction()}
        />
      )}
      {isDeviceAssignModal && (
        <DeviceAssignModal
          openAssignedModal={isDeviceAssignModal}
          handleClose={() => setIsDeviceAssignModal(false)}
          data={departmentDetail}
        />
      )}
      <ConfirmAlertDialog
        openConfirmAlertDialog={openConfirmAlertDialog}
        message={confirmAlertDialogMetas?.message}
        title={confirmAlertDialogMetas?.title}
        positiveBtnLable={confirmAlertDialogMetas?.positiveBtnLable}
        negativeBtnLable={confirmAlertDialogMetas?.negativeBtnLabel}
        handleClose={() => setOpenConfirmAlertDialog(false)}
        handlePositiveClose={() => {
          deleteDepartment(departmentDetail?.id);
          setOpenConfirmAlertDialog(false);
        }}
        handleNegativeClose={() => setOpenConfirmAlertDialog(false)}
      />
    </Box>
  );
}
